import { Injectable, OnDestroy, Inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { TrackUserActionService } from './track-user-actions.service';
import { DataStackActionIds } from './track-user-actions.helper';
import { CbreLogger, CbreLoggerToken } from '@cbre/logger';

/**
 * Service responsible for tracking user navigation to the SPMT (Data Stack) module.
 * This service monitors router events and registers tracking actions when users
 * access the /data-stack route or any of its sub-routes.
 */
@Injectable()
export class TrackSpmtNavigationService implements OnDestroy {
  private destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private trackUserActionService: TrackUserActionService,
    @Inject(CbreLoggerToken) private logger: CbreLogger
  ) {}

  /**
   * Initializes the navigation tracking subscription.
   * Should be called once during component initialization.
   */
  init(): void {
    const trackingMessge = {
      actionId: DataStackActionIds.navigateToDataStack,
      actionPage: 'Data Stack',
      actionTaken: 'Navigated to Data Stack module',
    };
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        filter((event: NavigationEnd) => event.urlAfterRedirects.startsWith('/data-stack')),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.trackUserActionService
          .registerAction({ actionId: DataStackActionIds.navigateToDataStack, actionDetails: trackingMessge })
          .subscribe({
            error: err => this.logger.error(`Failed to track SPMT module access: ${err}`),
          });
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
